import React, { createContext, useReducer, useContext, Dispatch } from "react";
import type {
  BenSpecQuestionRow,
  BenSpecQuestionSetMeta,
  SelectedFile,
  JobType,
  SelectedMember,
} from "../types";

/**
 * Default question set cached on the client after the library API resolves it.
 * Used by `BenSpecQuestionsPage` to power "Reset to defaults" without a second
 * round trip and to drive copy that references the default catalogue's size.
 */
export interface BenSpecDefaultSet {
  rows: BenSpecQuestionRow[];
  meta: BenSpecQuestionSetMeta;
}

// 1. DEFINE THE STATE SHAPE (matches backend MemberExtractionJobRequest)
// This structure holds all the data the user selects during the wizard workflow.
export interface JobState {
  jobType: JobType;
  sourceFolderUrl: string;
  sourceFolderDriveId: string;
  sourceFolderItemId: string;
  selectedFiles: SelectedFile[];
  memberConfig: {
    method: "AI_DETECT" | "MANUAL" | "CSV";
    /**
     * Members explicitly selected by the user.
     *
     * We keep this as a structured list so downstream submission can preserve
     * member scheme IDs vs names (and CSV can provide both).
     */
    selectedMembers: SelectedMember[];
    /**
     * Manual entry mode toggle: member scheme ID or name.
     *
     * This only applies to method === "MANUAL", but we store it so navigation
     * doesn't reset the user's choice.
     */
    manualEntryType: "member_scheme_id" | "name";
    customRule: string | null;
  };
  fieldConfig: {
    selectedFields: string[];
    customFields: { value: string; label: string }[];
    customRule: string | null;
  };
  currentBatchId: string | null;
  selectedReportJob: SelectedReportJob | null;
  /** Benefit-spec wizard: section + question label + question text. */
  benSpecQuestions: BenSpecQuestionRow[];
  benSpecQuestionSet: BenSpecQuestionSetMeta;
  /**
   * Cached default question set returned by the library API on first load.
   * Null until the library API has resolved at least once; consumers should
   * treat null as "default not yet known" and avoid offering a Reset action.
   */
  benSpecDefaultSet: BenSpecDefaultSet | null;
}

// keep state metadata for report pages separated into a clear type so downstream consumers can destructure safely.
export interface SelectedReportJob {
  jobId: string;
  jobType: JobType;
  reportDriveId: string | null;
  reportItemId: string | null;
}

// 2. DEFINE THE ACTIONS
// These are the types of updates we can perform on the state.
type Action =
  | {
      type: "SET_SOURCE_FOLDER";
      payload: { url: string; driveId: string; itemId: string };
    }
  | { type: "SET_JOB_TYPE"; payload: JobType }
  | { type: "SET_SELECTED_FILES"; payload: SelectedFile[] }
  | { type: "SET_CURRENT_BATCH_ID"; payload: string | null }
  | { type: "SET_SELECTED_REPORT_JOB"; payload: SelectedReportJob }
  | { type: "CLEAR_SELECTED_REPORT_JOB" }
  | { type: "SET_MEMBER_METHOD"; payload: "AI_DETECT" | "MANUAL" | "CSV" }
  | { type: "SET_SELECTED_MEMBERS"; payload: SelectedMember[] }
  | { type: "SET_MEMBER_MANUAL_ENTRY_TYPE"; payload: "member_scheme_id" | "name" }
  | { type: "SET_MEMBER_CUSTOM_RULE"; payload: string }
  | { type: "SET_SELECTED_FIELDS"; payload: string[] }
  | { type: "ADD_CUSTOM_FIELD"; payload: { value: string; label: string } }
  | { type: "REMOVE_CUSTOM_FIELD"; payload: string }
  | { type: "SET_FIELD_CUSTOM_RULE"; payload: string }
  | { type: "SET_BEN_SPEC_QUESTIONS"; payload: BenSpecQuestionRow[] }
  | { type: "SET_BEN_SPEC_QUESTION_SET"; payload: BenSpecQuestionSetMeta }
  | { type: "SET_BEN_SPEC_DEFAULT_SET"; payload: BenSpecDefaultSet }
  | { type: "RESET_JOB" };

// 3. DEFINE THE INITIAL STATE
// This is the default state when the app loads.
const initialState: JobState = {
  jobType: "MEMBER_EXTRACTION",
  sourceFolderUrl: "",
  sourceFolderDriveId: "",
  sourceFolderItemId: "",
  selectedFiles: [],
  memberConfig: {
    method: "AI_DETECT",
    selectedMembers: [],
    // Prefer a clearly member-specific scheme identifier when entering targets.
    manualEntryType: "member_scheme_id",
    customRule: null,
  },
  fieldConfig: {
    selectedFields: [],
    customFields: [],
    customRule: null,
  },
  currentBatchId: null,
  selectedReportJob: null,
  benSpecQuestions: [],
  benSpecQuestionSet: {
    id: null,
    name: null,
    source: null,
    version: null,
    snapshotHash: null,
  },
  benSpecDefaultSet: null,
};

// 4. CREATE THE REDUCER FUNCTION
// This function handles the logic for updating the state based on the action dispatched.
const jobReducer = (state: JobState, action: Action): JobState => {
  switch (action.type) {
    case "SET_JOB_TYPE":
      // Reset the per-job ben spec wizard state but keep the cached
      // default set so switching tasks doesn't lose the library payload
      // the user already paid a round trip for.
      return {
        ...state,
        jobType: action.payload,
        benSpecQuestions: [],
        benSpecQuestionSet: initialState.benSpecQuestionSet,
      };
    case "SET_SOURCE_FOLDER":
      return {
        ...state,
        sourceFolderUrl: action.payload.url,
        sourceFolderDriveId: action.payload.driveId,
        sourceFolderItemId: action.payload.itemId,
      };
    case "SET_SELECTED_FILES":
      return { ...state, selectedFiles: action.payload };
    case "SET_CURRENT_BATCH_ID":
      return { ...state, currentBatchId: action.payload };
    case "SET_SELECTED_REPORT_JOB":
      return { ...state, selectedReportJob: action.payload };
    case "CLEAR_SELECTED_REPORT_JOB":
      return { ...state, selectedReportJob: null };
    case "SET_MEMBER_METHOD":
      return {
        ...state,
        memberConfig: { ...state.memberConfig, method: action.payload },
      };
    case "SET_SELECTED_MEMBERS":
      return {
        ...state,
        memberConfig: { ...state.memberConfig, selectedMembers: action.payload },
      };
    case "SET_MEMBER_MANUAL_ENTRY_TYPE":
      return {
        ...state,
        memberConfig: { ...state.memberConfig, manualEntryType: action.payload },
      };
    case "SET_MEMBER_CUSTOM_RULE":
      return {
        ...state,
        memberConfig: { ...state.memberConfig, customRule: action.payload },
      };
    case "SET_SELECTED_FIELDS":
      return {
        ...state,
        fieldConfig: { ...state.fieldConfig, selectedFields: action.payload },
      };
    case "ADD_CUSTOM_FIELD":
      // Avoid adding duplicates
      if (state.fieldConfig.customFields.some((f) => f.value === action.payload.value)) {
        return state;
      }
      return {
        ...state,
        fieldConfig: {
          ...state.fieldConfig,
          customFields: [...state.fieldConfig.customFields, action.payload],
          // Also select the new field automatically
          selectedFields: [...state.fieldConfig.selectedFields, action.payload.value],
        },
      };
    case "REMOVE_CUSTOM_FIELD":
      return {
        ...state,
        fieldConfig: {
          ...state.fieldConfig,
          customFields: state.fieldConfig.customFields.filter(
            (field) => field.value !== action.payload
          ),
          // Also unselect the field
          selectedFields: state.fieldConfig.selectedFields.filter(
            (fieldValue) => fieldValue !== action.payload
          ),
        },
      };
    case "SET_FIELD_CUSTOM_RULE":
      return {
        ...state,
        fieldConfig: { ...state.fieldConfig, customRule: action.payload },
      };
    case "SET_BEN_SPEC_QUESTIONS":
      return { ...state, benSpecQuestions: action.payload };
    case "SET_BEN_SPEC_QUESTION_SET":
      return { ...state, benSpecQuestionSet: action.payload };
    case "SET_BEN_SPEC_DEFAULT_SET":
      return { ...state, benSpecDefaultSet: action.payload };
    case "RESET_JOB":
      // Preserve the cached default set across job resets so the
      // questions page can still offer "Reset to defaults" without
      // re-fetching the library on every wizard restart.
      return { ...initialState, benSpecDefaultSet: state.benSpecDefaultSet };
    default:
      return state;
  }
};

// 5. CREATE THE CONTEXT
// This object will be used by components to access the state and dispatch function.
interface JobContextProps {
  state: JobState;
  dispatch: Dispatch<Action>;
}

const JobContext = createContext<JobContextProps | undefined>(undefined);

// 6. CREATE THE PROVIDER COMPONENT
// This component will wrap our application and make the context available.
export const JobProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(jobReducer, initialState);

  return <JobContext.Provider value={{ state, dispatch }}>{children}</JobContext.Provider>;
};

// 7. CREATE A CUSTOM HOOK
// This makes it easy for components to use the context without extra boilerplate.
export const useJob = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error("useJob must be used within a JobProvider");
  }
  return context;
};
